/**
 * The swipe-up sheet: a handle that opens and closes it on a tap or a drag (a real button, so the
 * keyboard works too), and three tabs. Whether it is open is kept per device (PRD S5), in
 * localStorage as a convenience only: a blocked or empty store just starts it closed.
 */

const OPEN_KEY = 'tdmons.sheetOpen';
/** A drag this far (CSS px) up or down flips the sheet; shorter is a tap. */
const DRAG_PX = 24;

export function readPref(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writePref(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the preference lasts this page only.
  }
}

export class Sheet {
  private readonly handle: HTMLButtonElement;
  private readonly tabs: HTMLButtonElement[];

  constructor(private readonly root: HTMLElement) {
    const handle = root.querySelector<HTMLButtonElement>('.sheet-handle');
    if (!handle) throw new Error('the sheet needs a handle');
    this.handle = handle;
    this.tabs = [...root.querySelectorAll<HTMLButtonElement>('[role="tab"]')];

    let startY: number | null = null;
    let dragged = false;
    handle.addEventListener('pointerdown', (e) => {
      startY = e.clientY;
      dragged = false;
    });
    handle.addEventListener('pointermove', (e) => {
      if (startY === null || dragged) return;
      const dy = e.clientY - startY;
      if (Math.abs(dy) >= DRAG_PX) {
        dragged = true;
        this.setOpen(dy < 0);
      }
    });
    handle.addEventListener('pointerup', () => (startY = null));
    handle.addEventListener('click', () => {
      if (!dragged) this.setOpen(!this.open);
      dragged = false;
    });

    for (const tab of this.tabs) tab.addEventListener('click', () => this.select(tab.id));
    root.querySelector('.tabs')?.addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if (k !== 'ArrowLeft' && k !== 'ArrowRight') return;
      const i = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      const next =
        this.tabs[(i + (k === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length];
      if (next) {
        this.select(next.id);
        next.focus();
      }
    });

    this.setOpen(readPref(OPEN_KEY) === '1', false);
  }

  get open(): boolean {
    return this.root.classList.contains('open');
  }

  setOpen(open: boolean, save = true): void {
    this.root.classList.toggle('open', open);
    this.handle.setAttribute('aria-expanded', String(open));
    if (save) writePref(OPEN_KEY, open ? '1' : '0');
  }

  /** Show the tab with this id (`tab-log`, `tab-board`, `tab-dev`). */
  select(id: string): void {
    for (const tab of this.tabs) {
      const on = tab.id === id;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !on;
    }
  }
}
